import { useState, useEffect, useCallback } from 'react';
import { endpoints } from '../config/endpoints';
import { apiFetch } from './api-client';

export interface Session {
  id: string;
  displayName: string | null;
  assistantId: string;
  applicationId: string | null;
  sessionType: string;
  executionStatus: string;
  executionSubStatus: string | null;
  assistant: { name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

export function useSessions(workspaceId: string | null, autoLoad = true) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    if (!workspaceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(
        `${endpoints.sessions.list()}?workspaceId=${encodeURIComponent(workspaceId)}&limit=50&offset=0`,
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      // Backend returns { data: Session[], pagination: {...} }
      const fetched: Session[] = result.data || result.sessions || [];

      setSessions(fetched);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load sessions';
      console.error('[useSessions]', message, err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const deleteSession = useCallback(async (sessionId: string) => {
    const response = await apiFetch(endpoints.sessions.delete(sessionId), {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  useEffect(() => {
    if (autoLoad && workspaceId) {
      loadSessions();
    }
  }, [autoLoad, workspaceId, loadSessions]);

  return { sessions, loading, error, loadSessions, deleteSession };
}
